// CharityFormModal.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";

export default function CharityFormModal({
                                             mode = "create",      // 'create' | 'edit'
                                             charity = null,       // existing charity row, if editing
                                             onCreate,             // (payload) => Promise|void
                                             onSave,               // (id, payload) => Promise|void
                                             onCancel,             // () => void
                                         }) {
    const [categories, setCategories] = useState([]);
    const [loadingCats, setLoadingCats] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [evidenceQuery, setEvidenceQuery] = useState("");
    const [evidenceType, setEvidenceType] = useState("photo_document_ids");
    const [evidenceResults, setEvidenceResults] = useState([]);
    const [evidenceLoading, setEvidenceLoading] = useState(false);

    const [form, setForm] = useState({
        name: "",
        category_id: "",
        website: "",
        description: "",
        activity_date: "",
        event_name: "",
        venue: "",
        contact_person: "",
        contact_details: "",
        contribution_summary: "",
        status: "Active",
        target_amount: "",
        outcome: "",
        photo_document_ids: "",
        advert_document_ids: "",
        related_document_ids: "",
        campaign_notes: "",
    });

    const idsToText = (value) => Array.isArray(value) ? value.join(", ") : "";
    const textToIds = (value) =>
        String(value || "")
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);

    // Load categories from API
    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await axios.get("/api/resources/charity-categories");
                setCategories(data || []);
            } catch (err) {
                console.error("Failed to load charity categories:", err);
                alert("Could not load charity types.");
            } finally {
                setLoadingCats(false);
            }
        };
        load();
    }, []);

    // Initialise / update form when charity or categories change
    useEffect(() => {
        const resolveCategoryId = () => {
            if (!categories.length) return "";

            // New schema: charity already has category_id
            if (charity?.category_id) return String(charity.category_id);

            // Legacy: match by name if only .category is present
            if (charity?.category) {
                const match = categories.find(
                    (c) =>
                        (c.name || "").toLowerCase() ===
                        String(charity.category).toLowerCase()
                );
                if (match) return String(match.id);
            }

            // Default to first category if nothing else
            return categories[0]?.id ? String(categories[0].id) : "";
        };

        if (charity) {
            setForm({
                name: charity.name || "",
                category_id: resolveCategoryId(),
                website: charity.website || "",
                description: charity.description || "",
                activity_date: charity.activity_date ? String(charity.activity_date).slice(0, 10) : "",
                event_name: charity.event_name || "",
                venue: charity.venue || "",
                contact_person: charity.contact_person || "",
                contact_details: charity.contact_details || "",
                contribution_summary: charity.contribution_summary || "",
                status: charity.status || "Active",
                target_amount: charity.target_amount_cents ? (Number(charity.target_amount_cents) / 100).toFixed(2) : "",
                outcome: charity.outcome || "",
                photo_document_ids: idsToText(charity.photo_document_ids),
                advert_document_ids: idsToText(charity.advert_document_ids),
                related_document_ids: idsToText(charity.related_document_ids),
                campaign_notes: charity.campaign_notes || "",
            });
        } else {
            // new charity: keep any existing category_id, or default to first
            setForm((prev) => ({
                ...prev,
                category_id:
                    prev.category_id ||
                    (categories[0]?.id ? String(categories[0].id) : ""),
                status: prev.status || "Active",
            }));
        }
    }, [charity, categories]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const addEvidenceId = (fieldName, id) => {
        if (!id) return;
        setForm((prev) => {
            const ids = textToIds(prev[fieldName]);
            if (!ids.includes(id)) ids.push(id);
            return { ...prev, [fieldName]: ids.join(", ") };
        });
    };

    const searchEvidence = async () => {
        const query = evidenceQuery.trim();
        if (!query) {
            setEvidenceResults([]);
            return;
        }
        setEvidenceLoading(true);
        try {
            const { data } = await axios.get("/api/documents", {
                params: { q: query, limit: 12, offset: 0 },
            });
            setEvidenceResults(data?.documents || []);
        } catch (err) {
            console.error("Evidence search failed:", err);
            alert("Could not search archive documents.");
        } finally {
            setEvidenceLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        if (!form.name.trim()) {
            alert("Name is required.");
            return;
        }
        if (!form.category_id) {
            alert("Please select a type.");
            return;
        }

        // Look up the selected category by id so we can also send its name
        const selectedCategory = categories.find(
            (c) => String(c.id) === String(form.category_id)
        );

        if (!selectedCategory) {
            alert("Please select a valid type.");
            return;
        }

        const payload = {
            name: form.name.trim(),
            // new schema: keep the id
            category_id: form.category_id,
            // legacy / backend validation: send category name as well
            category: selectedCategory.name,
            website: form.website.trim() || null,
            description: form.description.trim() || null,
            activity_date: form.activity_date || null,
            event_name: form.event_name.trim() || null,
            venue: form.venue.trim() || null,
            contact_person: form.contact_person.trim() || null,
            contact_details: form.contact_details.trim() || null,
            contribution_summary: form.contribution_summary.trim() || null,
            status: form.status || "Active",
            target_amount_cents: form.target_amount
                ? Math.round(Number(form.target_amount) * 100)
                : null,
            outcome: form.outcome.trim() || null,
            photo_document_ids: textToIds(form.photo_document_ids),
            advert_document_ids: textToIds(form.advert_document_ids),
            related_document_ids: textToIds(form.related_document_ids),
            campaign_notes: form.campaign_notes.trim() || null,
        };

        // console.log("Submitting charity payload:", payload, "mode:", mode);

        setSubmitting(true);
        try {
            if (mode === "edit" && charity?.id && onSave) {
                await onSave(charity.id, payload);
            } else if (mode === "create" && onCreate) {
                await onCreate(payload);
            }
            // parent closes the modal on success
        } catch (err) {
            console.error("Save charity failed:", err);

            const msg =
                err?.response?.data?.error ||
                err?.message ||
                "Saving the charity failed.";
            alert(`Saving the charity failed: ${msg}`);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="cfm-overlay"
            onMouseDown={(e) => e.target === e.currentTarget && onCancel?.()}
        >
            <div className="cfm-dialog">
                <header className="cfm-header">
                    <h2>{mode === "edit" ? "Edit Charity" : "Add Charity"}</h2>
                    <button className="cfm-close" onClick={onCancel} disabled={submitting}>
                        ×
                    </button>
                </header>

                <form className="cfm-body" onSubmit={handleSubmit}>
                    <div className="cfm-grid">
                        <label>
                            <span>Name*</span>
                            <input
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                disabled={submitting}
                            />
                        </label>

                        <label>
                            <span>Type*</span>
                            <select
                                name="category_id"
                                value={form.category_id}
                                onChange={handleChange}
                                disabled={loadingCats || !categories.length || submitting}
                            >
                                {loadingCats && <option value="">Loading…</option>}

                                {!loadingCats && !categories.length && (
                                    <option value="">No types available</option>
                                )}

                                {!loadingCats &&
                                    categories.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                            </select>
                        </label>

                        <label className="span-2">
                            <span>Website</span>
                            <input
                                name="website"
                                value={form.website}
                                onChange={handleChange}
                                placeholder="https://…"
                                disabled={submitting}
                            />
                        </label>

                        <label>
                            <span>Activity Date</span>
                            <input
                                name="activity_date"
                                type="date"
                                value={form.activity_date}
                                onChange={handleChange}
                                disabled={submitting}
                            />
                        </label>

                        <label>
                            <span>Event / Activity</span>
                            <input
                                name="event_name"
                                value={form.event_name}
                                onChange={handleChange}
                                placeholder="Golf day, soup drive, appeal, handover..."
                                disabled={submitting}
                            />
                        </label>

                        <label>
                            <span>Status</span>
                            <select
                                name="status"
                                value={form.status}
                                onChange={handleChange}
                                disabled={submitting}
                            >
                                <option value="Planning">Planning</option>
                                <option value="Active">Active</option>
                                <option value="Completed">Completed</option>
                                <option value="Archived">Archived</option>
                            </select>
                        </label>

                        <label>
                            <span>Venue</span>
                            <input
                                name="venue"
                                value={form.venue}
                                onChange={handleChange}
                                placeholder="Temple, racecourse, hall, lodge venue..."
                                disabled={submitting}
                            />
                        </label>

                        <label>
                            <span>Target Amount (ZAR)</span>
                            <input
                                name="target_amount"
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.target_amount}
                                onChange={handleChange}
                                disabled={submitting}
                            />
                        </label>

                        <label>
                            <span>Contact Person</span>
                            <input
                                name="contact_person"
                                value={form.contact_person}
                                onChange={handleChange}
                                placeholder="Name of contact or coordinator"
                                disabled={submitting}
                            />
                        </label>

                        <label>
                            <span>Contact Details</span>
                            <input
                                name="contact_details"
                                value={form.contact_details}
                                onChange={handleChange}
                                placeholder="Phone, email, social handle, or reference"
                                disabled={submitting}
                            />
                        </label>

                        <label className="span-2">
                            <span>Contribution Summary</span>
                            <input
                                name="contribution_summary"
                                value={form.contribution_summary}
                                onChange={handleChange}
                                placeholder="R 12,000, 25 soup jars, clothing drive, supplies delivered..."
                                disabled={submitting}
                            />
                        </label>

                        <label className="span-2">
                            <span>Description</span>
                            <textarea
                                name="description"
                                rows={3}
                                value={form.description}
                                onChange={handleChange}
                                disabled={submitting}
                            />
                        </label>

                        <label className="span-2">
                            <span>Outcome / Impact</span>
                            <textarea
                                name="outcome"
                                rows={3}
                                value={form.outcome}
                                onChange={handleChange}
                                placeholder="Funds raised, beneficiaries supported, bursaries, widows, relief, supplies, follow-up..."
                                disabled={submitting}
                            />
                        </label>

                        <label className="span-2">
                            <span>Photo Document IDs</span>
                            <input
                                name="photo_document_ids"
                                value={form.photo_document_ids}
                                onChange={handleChange}
                                placeholder="Archive document IDs, comma separated"
                                disabled={submitting}
                            />
                        </label>

                        <section className="span-2 cfm-evidence-search">
                            <div>
                                <span>Find Archive Evidence</span>
                                <p>Search existing archive documents, then add each result as a photo, advert/poster, or supporting document.</p>
                            </div>
                            <div className="cfm-evidence-controls">
                                <input
                                    value={evidenceQuery}
                                    onChange={(event) => setEvidenceQuery(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            searchEvidence();
                                        }
                                    }}
                                    placeholder="Search Tirmochree, charity, golf day, thank you..."
                                    disabled={submitting}
                                />
                                <select
                                    value={evidenceType}
                                    onChange={(event) => setEvidenceType(event.target.value)}
                                    disabled={submitting}
                                >
                                    <option value="photo_document_ids">Photo</option>
                                    <option value="advert_document_ids">Advert / Poster</option>
                                    <option value="related_document_ids">Document / Attachment</option>
                                </select>
                                <button type="button" className="btn" onClick={searchEvidence} disabled={submitting || evidenceLoading}>
                                    {evidenceLoading ? "Searching..." : "Search"}
                                </button>
                            </div>
                            {evidenceResults.length > 0 && (
                                <div className="cfm-evidence-results">
                                    {evidenceResults.map((doc) => (
                                        <article key={doc.id}>
                                            <strong>{doc.title || doc.filename || doc.id}</strong>
                                            <span>{doc.lodge || "Archive"} · {doc.document_type_description || "Document"} · {doc.event_type_description || "Event"}</span>
                                            <button type="button" className="btn btn-sm" onClick={() => addEvidenceId(evidenceType, doc.id)}>
                                                Add
                                            </button>
                                        </article>
                                    ))}
                                </div>
                            )}
                        </section>

                        <label className="span-2">
                            <span>Advert / Poster Document IDs</span>
                            <input
                                name="advert_document_ids"
                                value={form.advert_document_ids}
                                onChange={handleChange}
                                placeholder="Archive document IDs, comma separated"
                                disabled={submitting}
                            />
                        </label>

                        <label className="span-2">
                            <span>Other Evidence Document IDs</span>
                            <input
                                name="related_document_ids"
                                value={form.related_document_ids}
                                onChange={handleChange}
                                placeholder="Minutes, letters, receipts, reports, comma separated"
                                disabled={submitting}
                            />
                        </label>

                        <label className="span-2">
                            <span>Activity Notes</span>
                            <textarea
                                name="campaign_notes"
                                rows={3}
                                value={form.campaign_notes}
                                onChange={handleChange}
                                placeholder="Planning notes, committee actions, sponsor notes, lessons learned"
                                disabled={submitting}
                            />
                        </label>
                    </div>

                    <footer className="cfm-footer">
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitting}
                        >
                            {submitting
                                ? mode === "edit"
                                    ? "Saving…"
                                    : "Adding…"
                                : mode === "edit"
                                    ? "Save"
                                    : "Add"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={onCancel}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                    </footer>
                </form>
            </div>
        </div>
    );
}
